'use client';

import React from 'react';
import { externalLinks } from '@/lib/links';

interface AutoLinkProps {
  children: string;
  className?: string;
}

interface MatchInfo {
  start: number;
  end: number;
  text: string;
  url: string;
}

export default function AutoLink({ children, className }: AutoLinkProps) {
  if (!children || typeof children !== 'string') {
    return <span className={className}>{children}</span>;
  }

  // Find all matches
  const matches: MatchInfo[] = [];

  for (const link of externalLinks) {
    // Reset lastIndex for global regexps
    const regex = new RegExp(link.pattern.source, link.pattern.flags);
    let match: RegExpExecArray | null;

    while ((match = regex.exec(children)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        url: link.url,
      });

      // Prevent infinite loop on zero-length matches
      if (match[0].length === 0) {
        regex.lastIndex++;
      }
    }
  }

  if (matches.length === 0) {
    return <span className={className}>{children}</span>;
  }

  // Sort by start position, then longest first (to handle overlapping)
  matches.sort((a, b) => a.start - b.start || b.end - a.end);

  // Remove overlapping matches (keep earliest/longest)
  const filtered: MatchInfo[] = [];
  let lastEnd = -1;

  for (const m of matches) {
    if (m.start >= lastEnd) {
      filtered.push(m);
      lastEnd = m.end;
    }
  }

  // Build React elements
  const elements: React.ReactNode[] = [];
  let cursor = 0;

  for (let i = 0; i < filtered.length; i++) {
    const m = filtered[i];

    // Add text before this match
    if (m.start > cursor) {
      elements.push(children.slice(cursor, m.start));
    }

    // Add the linked match
    elements.push(
      <a
        key={`autolink-${i}`}
        href={m.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#9B7B3A] hover:text-[#C4A95E] underline-offset-2 hover:underline"
      >
        {m.text}
      </a>
    );

    cursor = m.end;
  }

  // Add remaining text
  if (cursor < children.length) {
    elements.push(children.slice(cursor));
  }

  return <span className={className}>{elements}</span>;
}
